import { message } from 'antd';
import React, { createContext, useContext, useEffect, useReducer } from 'react';
import authService from '../services/auth';

// 创建认证上下文
const AuthContext = createContext();

// 初始状态
const initialState = {
  user: authService.getUser(),
  isAuthenticated: authService.isAuthenticated(),
  isLoading: false,
  error: null,
};

// Action类型
const ActionTypes = {
  SET_LOADING: 'SET_LOADING',
  LOGIN_SUCCESS: 'LOGIN_SUCCESS',
  LOGIN_FAILURE: 'LOGIN_FAILURE',
  LOGOUT: 'LOGOUT',
  UPDATE_USER: 'UPDATE_USER',
  CLEAR_ERROR: 'CLEAR_ERROR',
  SET_ERROR: 'SET_ERROR',
};

// Reducer函数
const authReducer = (state, action) => {
  switch (action.type) {
    case ActionTypes.SET_LOADING:
      return {
        ...state,
        isLoading: action.payload,
        error: null,
      };

    case ActionTypes.LOGIN_SUCCESS:
      return {
        ...state,
        user: action.payload,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      };

    case ActionTypes.LOGIN_FAILURE:
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: action.payload,
      };

    case ActionTypes.LOGOUT:
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      };

    case ActionTypes.UPDATE_USER:
      return {
        ...state,
        user: action.payload,
        error: null,
      };

    case ActionTypes.CLEAR_ERROR:
      return {
        ...state,
        error: null,
      };

    case ActionTypes.SET_ERROR:
      return {
        ...state,
        error: action.payload,
        isLoading: false,
      };

    default:
      return state;
  }
};

// AuthProvider组件
export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // 登录
  const login = async (email, password) => {
    dispatch({ type: ActionTypes.SET_LOADING, payload: true });
    
    try {
      const result = await authService.login(email, password);
      
      if (result.success) {
        dispatch({ type: ActionTypes.LOGIN_SUCCESS, payload: result.user });
        message.success('登录成功！');
        return { success: true };
      } else {
        dispatch({ type: ActionTypes.LOGIN_FAILURE, payload: result.error });
        message.error(result.error);
        return { success: false, error: result.error };
      }
    } catch (error) {
      const errorMessage = '登录失败，请稍后重试';
      dispatch({ type: ActionTypes.LOGIN_FAILURE, payload: errorMessage });
      message.error(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  // 注册
  const register = async (userData) => {
    dispatch({ type: ActionTypes.SET_LOADING, payload: true });
    
    try {
      const result = await authService.register(userData);
      
      if (result.success) {
        if (result.autoLogin) {
          dispatch({ type: ActionTypes.LOGIN_SUCCESS, payload: result.user });
          message.success('注册成功并已自动登录！');
        } else {
          dispatch({ type: ActionTypes.SET_LOADING, payload: false });
          message.success('注册成功，请登录');
        }
        return { success: true, autoLogin: result.autoLogin };
      } else {
        dispatch({ type: ActionTypes.SET_ERROR, payload: result.error });
        message.error(result.error);
        return { success: false, error: result.error };
      }
    } catch (error) {
      const errorMessage = '注册失败，请稍后重试';
      dispatch({ type: ActionTypes.SET_ERROR, payload: errorMessage });
      message.error(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  // 登出
  const logout = async () => {
    dispatch({ type: ActionTypes.SET_LOADING, payload: true });
    
    try {
      await authService.logout();
      dispatch({ type: ActionTypes.LOGOUT });
      message.success('已退出登录');
    } catch (error) {
      console.error('登出失败:', error);
      // 即使API调用失败，也要清除本地状态
      dispatch({ type: ActionTypes.LOGOUT });
      message.success('已退出登录');
    }
  };

  // 更新用户信息
  const updateUser = async (userData) => {
    dispatch({ type: ActionTypes.SET_LOADING, payload: true });
    
    try {
      const result = await authService.updateUser(userData);
      
      if (result.success) {
        dispatch({ type: ActionTypes.UPDATE_USER, payload: result.user });
        message.success('用户信息更新成功！');
        return { success: true };
      } else {
        dispatch({ type: ActionTypes.SET_ERROR, payload: result.error });
        message.error(result.error);
        return { success: false, error: result.error };
      }
    } catch (error) {
      const errorMessage = '更新失败，请稍后重试';
      dispatch({ type: ActionTypes.SET_ERROR, payload: errorMessage });
      message.error(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  // 获取当前用户信息
  const refreshUser = async () => {
    if (!state.isAuthenticated) return;
    
    try {
      const user = await authService.getCurrentUser();
      dispatch({ type: ActionTypes.UPDATE_USER, payload: user });
    } catch (error) {
      console.error('刷新用户信息失败:', error);
      // 如果获取用户信息失败，可能是token过期，执行登出
      if (error.response?.status === 401) {
        dispatch({ type: ActionTypes.LOGOUT });
      }
    }
  };

  // 清除错误
  const clearError = () => {
    dispatch({ type: ActionTypes.CLEAR_ERROR });
  };

  // 忘记密码
  const forgotPassword = async (email) => {
    dispatch({ type: ActionTypes.SET_LOADING, payload: true });
    
    try {
      const result = await authService.forgotPassword(email);
      dispatch({ type: ActionTypes.SET_LOADING, payload: false });
      
      if (result.success) {
        message.success('密码重置邮件已发送，请检查您的邮箱');
        return { success: true };
      } else {
        message.error(result.error);
        return { success: false, error: result.error };
      }
    } catch (error) {
      const errorMessage = '发送失败，请稍后重试';
      dispatch({ type: ActionTypes.SET_LOADING, payload: false });
      message.error(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  // 重置密码
  const resetPassword = async (token, password) => {
    dispatch({ type: ActionTypes.SET_LOADING, payload: true });
    
    try {
      const result = await authService.resetPassword(token, password);
      dispatch({ type: ActionTypes.SET_LOADING, payload: false });
      
      if (result.success) {
        message.success('密码重置成功，请重新登录');
        return { success: true };
      } else {
        message.error(result.error);
        return { success: false, error: result.error };
      }
    } catch (error) {
      const errorMessage = '重置失败，请稍后重试';
      dispatch({ type: ActionTypes.SET_LOADING, payload: false });
      message.error(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  // 请求邮箱验证
  const requestVerification = async () => {
    try {
      const result = await authService.requestVerification();
      
      if (result.success) {
        message.success('验证邮件已发送，请检查您的邮箱');
        return { success: true };
      } else {
        message.error(result.error);
        return { success: false, error: result.error };
      }
    } catch (error) {
      const errorMessage = '发送失败，请稍后重试';
      message.error(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  // 验证邮箱
  const verifyEmail = async (token) => {
    dispatch({ type: ActionTypes.SET_LOADING, payload: true });
    
    try {
      const result = await authService.verifyEmail(token);
      
      if (result.success) {
        // 刷新用户信息以更新验证状态
        await refreshUser();
        message.success('邮箱验证成功！');
        return { success: true };
      } else {
        dispatch({ type: ActionTypes.SET_LOADING, payload: false });
        message.error(result.error);
        return { success: false, error: result.error };
      }
    } catch (error) {
      const errorMessage = '验证失败，请检查验证链接';
      dispatch({ type: ActionTypes.SET_LOADING, payload: false });
      message.error(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  // 微信登录
  const wechatLogin = async (code, state = null) => {
    dispatch({ type: ActionTypes.SET_LOADING, payload: true });
    
    try {
      const result = await authService.wechatLogin(code, state);
      
      if (result.success) {
        dispatch({ type: ActionTypes.LOGIN_SUCCESS, payload: result.user });
        const welcomeMsg = result.isNewUser ? '欢迎使用 MedSyntix！' : '微信登录成功！';
        message.success(welcomeMsg);
        return { success: true, user: result.user, isNewUser: result.isNewUser };
      } else {
        dispatch({ type: ActionTypes.LOGIN_FAILURE, payload: result.error });
        message.error(result.error);
        return { success: false, error: result.error };
      }
    } catch (error) {
      const errorMessage = '微信登录失败，请稍后重试';
      dispatch({ type: ActionTypes.LOGIN_FAILURE, payload: errorMessage });
      message.error(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  // 获取微信授权URL
  const getWeChatAuthUrl = async (state = null) => {
    try {
      const result = await authService.getWeChatAuthUrl(state);
      
      if (result.success) {
        return { success: true, data: result.data };
      } else {
        message.error(result.error);
        return { success: false, error: result.error };
      }
    } catch (error) {
      const errorMessage = '获取微信授权URL失败';
      message.error(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  // 监听认证事件
  useEffect(() => {
    const handleAuthLogin = (event) => {
      dispatch({ type: ActionTypes.LOGIN_SUCCESS, payload: event.detail.user });
    };

    const handleAuthLogout = (event) => {
      dispatch({ type: ActionTypes.LOGOUT });
    };

    const handleUserUpdated = (event) => {
      dispatch({ type: ActionTypes.UPDATE_USER, payload: event.detail.user });
    };

    window.addEventListener('auth-login', handleAuthLogin);
    window.addEventListener('auth-logout', handleAuthLogout);
    window.addEventListener('user-updated', handleUserUpdated);

    // 组件挂载时刷新用户信息
    if (state.isAuthenticated) {
      refreshUser();
    }

    return () => {
      window.removeEventListener('auth-login', handleAuthLogin);
      window.removeEventListener('auth-logout', handleAuthLogout);
      window.removeEventListener('user-updated', handleUserUpdated);
    };
  }, []);

  const value = {
    // 状态
    user: state.user,
    isAuthenticated: state.isAuthenticated,
    isLoading: state.isLoading,
    error: state.error,
    
    // 认证方法
    login,
    register,
    logout,
    updateUser,
    refreshUser,
    clearError,
    
    // 密码管理
    forgotPassword,
    resetPassword,
    
    // 邮箱验证
    requestVerification,
    verifyEmail,
    
    // 微信登录
    wechatLogin,
    getWeChatAuthUrl,
    
    // 用户状态检查
    isEmailVerified: () => authService.isEmailVerified(),
    isSuperUser: () => authService.isSuperUser(),
    getUserRole: () => authService.getUserRole(),
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// 自定义Hook
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext; 